import { useCallback } from 'react'
import { useI18n } from '../../i18n'
import type { CVCustomSection } from '../../types/cv'
import './editor.css'

export interface CustomSectionsEditorProps {
  data: CVCustomSection[]
  onChange: (data: CVCustomSection[]) => void
}

/**
 * Custom sections editor
 * Allows users to add free-form content sections with custom titles
 */
export function CustomSectionsEditor({ data, onChange }: CustomSectionsEditorProps) {
  const { t } = useI18n()
  const handleAdd = useCallback(() => {
    const newEntry: CVCustomSection = {
      id: crypto.randomUUID(),
      title: '',
      content: '',
      sortOrder: data.length,
    }
    onChange([...data, newEntry])
  }, [data, onChange])

  const handleRemove = useCallback(
    (id: string) => {
      onChange(data.filter((item) => item.id !== id))
    },
    [data, onChange]
  )

  const handleChange = useCallback(
    (id: string, field: keyof CVCustomSection, value: string) => {
      onChange(data.map((item) => (item.id === id ? { ...item, [field]: value } : item)))
    },
    [data, onChange]
  )

  return (
    <>
      <ul className="editor-entry-list" aria-label={t('editor.lists.custom')}>
        {data.map((section, index) => {
          const entryLabel =
            section.title || t('editor.entries.customSection', { number: index + 1 })
          return (
            <li key={section.id} className="editor-entry" aria-label={entryLabel}>
              <div className="editor-entry-header">
                <span className="editor-entry-title">{entryLabel}</span>
                <div className="editor-entry-actions">
                  <button
                    type="button"
                    className="editor-btn editor-btn-danger editor-btn-icon"
                    onClick={() => handleRemove(section.id)}
                    aria-label={t('actions.remove', { item: entryLabel })}
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="editor-grid">
                <div className="editor-field">
                  <label htmlFor={`section-title-${section.id}`}>{t('fields.sectionTitle')}</label>
                  <input
                    id={`section-title-${section.id}`}
                    type="text"
                    value={section.title}
                    onChange={(e) => handleChange(section.id, 'title', e.target.value)}
                    placeholder={t('placeholders.sectionTitle')}
                  />
                </div>

                <div className="editor-field editor-field-full">
                  <label htmlFor={`section-content-${section.id}`}>{t('fields.content')}</label>
                  <textarea
                    id={`section-content-${section.id}`}
                    className="editor-textarea-long"
                    value={section.content}
                    onChange={(e) => handleChange(section.id, 'content', e.target.value)}
                    placeholder={t('placeholders.sectionContent')}
                    rows={8}
                  />
                </div>
              </div>
            </li>
          )
        })}
      </ul>

      <button type="button" className="editor-add-btn" onClick={handleAdd}>
        <span aria-hidden="true">+</span> {t('actions.addSection')}
      </button>
    </>
  )
}
